/**
 * This file contains analytics functions related to navigation keyboard shortcuts.
 * We are firing events on the navigation fabric channel to align with the other events
 * specified in https://hello.atlassian.net/wiki/spaces/PData/pages/140331306/Minimum+Event+Spec+-+Navigation
 * These functions are used in both nav v1 and nav v2 at the moment with a long term goal of moving global navigation
 * keyboard shortcuts within atlaskit itself.
 */
import { FabricChannel } from '@atlaskit/analytics-listeners';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CreateAnalyticsEventType = any;

type KeyboardShortcutId =
	| 'starDrawer'
	| 'quickSearch'
	| 'expandCollapseSidebar'
	| 'createIssue'
	| 'keyboardShortcuts'
	| 'findIssues'
	| 'browseToProject'
	| 'openHelpPanel';

/**
 * This implements the keyboardShortcut pressed event for global/nav keyboard shortcuts as
 * specified here:
 * https://hello.atlassian.net/wiki/spaces/PData/pages/140331306/Minimum+Event+Spec+-+Navigation#MinimumEventSpec-Navigation-keyboardShortcutEvent
 */
export const keyboardShortcutPressed = ({
	createAnalyticsEvent,
	id,
	key,
	attributes = {},
}: {
	createAnalyticsEvent: CreateAnalyticsEventType;
	id: KeyboardShortcutId;
	key: string;
	attributes?: {
		[key: string]: string | number | boolean;
	};
}): void => {
	createAnalyticsEvent({
		action: 'pressed',
		actionSubject: 'keyboardShortcut',
		actionSubjectId: id,
		attributes: {
			navVersion: window.__NAV_VERSION__ || 'UNKNOWN',
			key,
			...attributes,
		},
	}).fire(FabricChannel.navigation);
};

/**
 * This implements the navigation productNavigation expanded/collapsed event for navigation when
 * triggered via keyboard shortcut as specified here:
 * https://hello.atlassian.net/wiki/spaces/PData/pages/140331306/Minimum+Event+Spec+-+Navigation#MinimumEventSpec-Navigation-productNavigationExpandedCollapsed
 */

export const productNavigationExpandedCollapsed = ({
	createAnalyticsEvent,
	isCollapsed,
}: {
	createAnalyticsEvent: CreateAnalyticsEventType;
	/** The collapsed state before the expand/collapse occurs */
	isCollapsed: boolean;
}): void => {
	createAnalyticsEvent({
		action: isCollapsed ? 'expanded' : 'collapsed',
		actionSubject: 'productNavigation',
		attributes: {
			trigger: 'keyboardShortcut',
		},
	}).fire(FabricChannel.navigation);
};
